import React, {useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useTable} from 'react-table';
import * as commonColumnFormatters from '../../../_common/column-formatters';
import * as customColumnFormatters from './column-formatters';
import {shallowEqual, useSelector} from 'react-redux';
import {getForm101Year} from '../utils';

export function CompleteDetailsTable() {
  const intl = useIntl();
  const {currentState} = useSelector(
    (state) => ({currentState: state['completeDetails']}),
    shallowEqual
  );
  const tableRows = currentState.entities ? currentState.entities : [];

  const [year, setYear] = useState(null);

  useEffect(() => {
    setYear(getForm101Year(tableRows));
  }, [tableRows]);

  const columns = useMemo(() => [
    {
      accessor: 'employeeID',
      Header: intl.formatMessage({
        id: 'complete-details-column-employeeID',
        defaultMessage: 'Employee ID'
      })
    },
    {
      accessor: 'idNumber',
      Header: intl.formatMessage({
        id: 'complete-details-column-idNumber', defaultMessage: 'ID'
      })
    },
    {
      accessor: 'fullName',
      Header: intl.formatMessage({
        id: 'complete-details-column-fullName', defaultMessage: 'Full name'
      })
    },
    {
      accessor: 'bankBranch',
      Header: intl.formatMessage({
        id: 'complete-details-column-bankBranch',
        defaultMessage: 'Bank code / Branch number'
      })
    },
    {
      accessor: 'accountNumber',
      Header: intl.formatMessage({
        id: 'complete-details-column-accountNumber',
        defaultMessage: 'Account number'
      })
    },
    {
      accessor: 'amount',
      Header: intl.formatMessage({
        id: 'complete-details-column-amount', defaultMessage: 'Amount'
      })
    },
    {
      accessor: 'agentID',
      Header: intl.formatMessage({
        id: 'complete-details-column-agentID', defaultMessage: 'Agent ID'
      })
    },
    {
      accessor: 'phoneEmail',
      Header: intl.formatMessage({
        id: 'complete-details-column-phoneEmail',
        defaultMessage: 'Phone / Email'
      })
    },
    {
      accessor: 'year',
      Header: year
        ? intl.formatMessage({
          id: 'complete-details-column-form101Year-value',
          defaultMessage: 'Year',
        }, {year})
        : intl.formatMessage({
          id: 'complete-details-column-form101Year', defaultMessage: 'Year'
        })
    },
    {
      accessor: 'comment',
      Header: intl.formatMessage({
        id: 'complete-details-column-comments', defaultMessage: 'Comments'
      })
    },
  ], [year, intl]);
  const data = useMemo(() => [
    ...tableRows.map((item) => {
      return {
        employeeID: commonColumnFormatters.BoldColumnFormatter(item.employeeID),
        idNumber: commonColumnFormatters.BoldColumnFormatter(item.idNumber),
        fullName: commonColumnFormatters.BoldColumnFormatter(item.fullName),
        bankBranch: customColumnFormatters.DoubleColumnFormatter(item.bankCode, item.branchNumber),
        accountNumber: commonColumnFormatters.BoldColumnFormatter(item.accountNumber),
        amount: Number.isFinite(parseFloat(item.amount))
          ? commonColumnFormatters.LimitDecimalColumnFormatter(item.amount)
          : '',
        agentID: commonColumnFormatters.BoldColumnFormatter(item.agentID),
        phoneEmail: customColumnFormatters.DoubleColumnFormatter(item.phone, item.email),
        year: year
          ? customColumnFormatters.FilesForYearExistsColumnFormatter(item.form11FilesForSpecifiedYearExist, intl)
          : '',
        comment: commonColumnFormatters.WrapColumnFormatter(item.comment),
      };
    })
  ], [tableRows, year]);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
    columns,
    data,
  });

  return (
    <div id="scrollContainer">
      <table
        {...getTableProps()}
        className="table table-head-custom table-vertical-center infinite-scroll-table"
      >
        <thead className="sticky-header head custom-thead">
        {headerGroups.map(headerGroup => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map(column => (
              <th
                {...column.getHeaderProps()}
                id="custom-brand-color-tableHead"
              >
                {column.render('Header')}
              </th>
            ))}
          </tr>
        ))}
        </thead>
        <tbody {...getTableBodyProps()} className="body">
        {rows.map((row) => {
          prepareRow(row);
          return (
            <tr {...row.getRowProps()}>
              {row.cells.map(cell => {
                return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
              })}
            </tr>
          );
        })}
        </tbody>
      </table>
    </div>
  );
}
